import React from 'react';
import { Calendar, Clock, Ticket, User } from 'lucide-react';
import { formatBookingId, format24HourTime } from '../../context/DashboardContext';

const UpcomingReservations = ({ upcomingReservations = [], navigate, bookingPath = '/booking' }) => {
  const calculateDaysToGo = (dateInput) => {
    if (!dateInput) return 'Upcoming';
    try {
      const start = new Date(dateInput);
      if (isNaN(start.getTime())) return 'Upcoming';

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);

      const diffTime = start - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      if (diffDays < 0) return 'Past';
      return `In ${diffDays} days`;
    } catch (e) {
      return 'Upcoming';
    }
  };

  const formatDateDisplay = (dateInput) => {
    if (!dateInput) return 'N/A';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return String(dateInput);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 overflow-hidden relative mb-6 sm:mb-8">
      <div className="flex justify-between items-center mb-4 sm:mb-6 relative z-10">
        <h3 className="text-base sm:text-lg font-bold text-slate-800 flex items-center gap-2">
          <div className="p-1.5 sm:p-2 bg-[#067978]/10 rounded-xl text-[#067978]">
            <Calendar size={16} className="sm:w-[18px] sm:h-[18px]" />
          </div>
          Upcoming Reservations
        </h3>

        {upcomingReservations.length > 0 && (
          <span className="text-[9px] sm:text-[10px] bg-slate-50 text-slate-500 font-bold px-2.5 sm:px-3 py-1 rounded-full uppercase tracking-wider border border-slate-100">
            Next {upcomingReservations.length} Bookings
          </span>
        )}
      </div>

      <div className="overflow-x-auto relative z-10 w-full">
        {upcomingReservations.length > 0 ? (
          <table className="w-full text-left border-separate border-spacing-y-2 min-w-[580px]">
            <thead>
              <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                <th className="pb-2 pl-4">Guest & Booking ID</th>
                <th className="pb-2">Details / Item</th>
                <th className="pb-2">Date & 24h Time</th>
                <th className="pb-2">Countdown</th>
                <th className="pb-2 text-right pr-6 w-[140px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {upcomingReservations.map((res, idx) => {
                const guestName =
                  res.user_id?.name ||
                  res.booking_id?.name ||
                  res.guest_name ||
                  res.name ||
                  'Guest';

                // Strictly format Booking ID: 5-char lowercase (a-z, 0-9)
                const formattedId = formatBookingId(res.booking_id || res._id);

                // Strictly format Time: 24-hour format (HH:mm)
                const time24 = format24HourTime(res.bookedTime || res.time || res.date);

                const itemDetail =
                  res.businessDetails?.business_name ||
                  res.activity_name ||
                  res.item_name ||
                  res.special_request ||
                  'Reservation Booking';

                const daysCount = calculateDaysToGo(res.date || res.createdAt);

                return (
                  <tr
                    key={res._id || idx}
                    className="group cursor-pointer hover:translate-x-1 transition-transform duration-300"
                    onClick={() => navigate(bookingPath)}
                  >
                    <td className="bg-slate-50/50 group-hover:bg-slate-50 py-3.5 px-4 rounded-l-2xl border-y border-l border-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#067978]/10 text-[#067978] flex items-center justify-center font-bold text-xs shrink-0">
                          <User size={14} />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-800 text-xs sm:text-sm">{guestName}</span>
                          <span className="text-[10px] text-slate-400 font-mono tracking-tight lowercase">
                            id: {formattedId}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="bg-slate-50/50 group-hover:bg-slate-50 py-3.5 border-y border-slate-100 transition-colors">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600 font-semibold truncate max-w-[180px]">
                        <Ticket size={12} className="text-[#067978] shrink-0" />
                        <span className="truncate">{itemDetail}</span>
                      </div>
                    </td>

                    <td className="bg-slate-50/50 group-hover:bg-slate-50 py-3.5 border-y border-slate-100 transition-colors">
                      <div className="flex flex-col">
                        <span className="text-xs sm:text-sm font-bold text-slate-700">
                          {formatDateDisplay(res.date || res.createdAt)}
                        </span>
                        <span className="text-[10px] font-semibold text-slate-400 flex items-center gap-1">
                          <Clock size={10} />
                          {time24}
                        </span>
                      </div>
                    </td>

                    <td className="bg-slate-50/50 group-hover:bg-slate-50 py-3.5 border-y border-slate-100 transition-colors">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider border ${
                        daysCount === 'Today'
                          ? 'bg-red-50 text-red-600 border-red-100'
                          : daysCount === 'Tomorrow'
                          ? 'bg-amber-50 text-amber-600 border-amber-100'
                          : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                      }`}>
                        {daysCount}
                      </span>
                    </td>

                    <td className="bg-slate-50/50 group-hover:bg-slate-50 py-3.5 pr-4 rounded-r-2xl border-y border-r border-slate-100 transition-colors text-right whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(bookingPath);
                        }}
                        className="text-[9px] sm:text-[10px] bg-[#067978] hover:bg-[#056362] text-white font-extrabold px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-xl transition-all shadow-sm active:scale-95 uppercase tracking-wider"
                      >
                        Manage Booking
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="py-8 flex flex-col items-center justify-center text-center">
            <Calendar size={28} strokeWidth={1.5} className="text-slate-300 mb-2" />
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              No upcoming reservations
            </p>
            <button
              onClick={() => navigate(bookingPath)}
              className="text-[10px] font-bold text-[#067978] hover:underline uppercase tracking-widest mt-1"
            >
              View Reservation Desk
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingReservations;
